import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Toast — ephemeral notification primitive.
 *
 * Used for non-blocking feedback like "Link copied" (audit #20).
 * Context-based API so any component can call `useToast().show(...)`.
 *
 * Behaviour:
 *  - Fixed bottom-center on mobile, slides up
 *  - Auto-dismiss after 2.5 s (configurable)
 *  - aria-live="polite" for screen reader announcement
 *  - Pointer-events-none on container so toasts don't block clicks behind
 */

export interface ToastOptions {
  /** Material Symbol icon name, default "check" */
  icon?: string;
  /** ms before auto-dismiss, default 2500 */
  duration?: number;
}

interface ToastItem {
  id: number;
  message: string;
  icon: string;
}

interface ToastContextValue {
  show: (message: string, opts?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const show = useCallback((message: string, opts: ToastOptions = {}) => {
    const id = Date.now() + Math.random();
    const icon = opts.icon ?? 'check';
    const duration = opts.duration ?? 2500;
    setToasts(prev => [...prev, { id, message, icon }]);
    window.setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  // CRITICAL: memoise the context value. Without this, every state update
  // (including the toasts queue itself) creates a fresh `{ show }` object,
  // which causes every consumer's `useToast()` call to return a NEW
  // reference. Any consumer that puts `toast` in a useEffect dep array
  // then re-runs the effect on every render — leading to a runaway loop
  // (effect fires → toast.show → setState → re-render → new toast ref →
  //  effect fires again → ...). Memoising on the stable `show` callback
  // breaks the loop.
  const ctxValue = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={ctxValue}>
      {children}
      <div
        className="fixed left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2 pointer-events-none"
        style={{ bottom: 'max(env(safe-area-inset-bottom), 1rem)' }}
        aria-live="polite"
        aria-atomic="true"
      >
        {toasts.map(t => (
          <div
            key={t.id}
            role="status"
            className="pointer-events-auto px-4 h-11 inline-flex items-center gap-2 rounded-full bg-stone-900 text-white text-sm shadow-2xl"
          >
            <span
              className="material-symbols-rounded"
              style={{ fontSize: 18 }}
              aria-hidden="true"
            >
              {t.icon}
            </span>
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
